import type { ProFormColumnsType } from '@ant-design/pro-components';
import type { BtlApplication } from 'api/btlApplicationForm/types';
import { ApplicationStatus } from 'legacy/shared-types/enums/application-status.enum';
import { App } from 'antd';
import { firmApi } from 'api/firm/firm.api';
import { userApi } from 'api/user';
import {
  getAdjacentOoCaseSidebarItemId,
  getAdjacentOoCaseSidebarUrlSection,
  getOoCaseSidebarNavigationLabel,
} from 'components/CaseSidebar';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { isAfterOoDipAccepted, isOoPostDipPreFeesStatus } from 'components/CaseSidebar/config/ooStatus';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { FormValidationContext } from 'hooks';
import { ooFinancingFormSchema } from 'pages/BtlApplicationFormPage/FinancingAndProperty/formSchema';
import { hydrateOoFinancingFormValues } from 'pages/BtlApplicationFormPage/FinancingAndProperty/mapApplicationToFormValues';
import { submitFinancingAndPropertyForm } from 'pages/BtlApplicationFormPage/FinancingAndProperty/submitFinancingAndPropertyForm';
import { makeFormReadonly } from 'pages/BtlApplicationFormPage/makeFormReadonly';
import { useBrokerFirm } from 'queries/broker/useBrokerFirm';
import { useAuth } from 'providers';
import { useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface UseOoCaseFinancingFormParams {
  caseId: string;
  caseData?: BtlApplication;
  isCaseLoading: boolean;
  sidebarContext: OoCaseSidebarContext;
  canAccessHppPages: boolean;
}

const EDITABLE_TERM_FIELD = 'termInMonths';

interface BuildOoFinancingFormColumnsOptions {
  canEditAll: boolean;
  canEditTermOnly: boolean;
  lockTermField?: boolean;
}

function buildOoFinancingFormColumns(
  schema: ProFormColumnsType[],
  options: BuildOoFinancingFormColumnsOptions,
): ProFormColumnsType[] {
  if (options.canEditAll) {
    if (options.lockTermField) {
      const readonlyTermColumns = makeFormReadonly(schema.filter(column => column.dataIndex === EDITABLE_TERM_FIELD));

      return [...schema.filter(column => column.dataIndex !== EDITABLE_TERM_FIELD), ...readonlyTermColumns];
    }

    return schema;
  }

  if (!options.canEditTermOnly) {
    return makeFormReadonly(schema);
  }

  const editableColumns = schema.filter(column => column.dataIndex === EDITABLE_TERM_FIELD);
  const readonlyColumns = makeFormReadonly(schema.filter(column => column.dataIndex !== EDITABLE_TERM_FIELD));

  return [...readonlyColumns, ...editableColumns];
}

export function isOoFinancingFormEditable(
  caseData?: BtlApplication,
  canAccessHppPages?: boolean,
  canEditDipAcceptedSections?: boolean,
): boolean {
  if (caseData?.status === ApplicationStatus.DIP_ACCEPTED) {
    return Boolean(canEditDipAcceptedSections);
  }

  if (isOoPostDipPreFeesStatus(caseData?.status)) {
    return false;
  }

  return Boolean(canAccessHppPages) && !isAfterOoDipAccepted(caseData?.status);
}

export function useOoCaseFinancingForm({
  caseId,
  caseData,
  isCaseLoading,
  sidebarContext,
  canAccessHppPages,
}: UseOoCaseFinancingFormParams) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatMessage } = useIntl();
  const { isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const { data: firm, isLoading: isFirmLoading } = useBrokerFirm();
  const queryClient = useQueryClient();
  const formValidationContext = useContext(FormValidationContext);
  const formSectionParam = searchParams.get('section') ?? CaseSidebarItemId.FINANCING;
  const propertyId = caseData?.applicationProperty?.property?.id;
  const isPostDipPreFees = isOoPostDipPreFeesStatus(caseData?.status);
  const isDipAcceptedFullEdit =
    caseData?.status === ApplicationStatus.DIP_ACCEPTED && sidebarContext.canEditDipAcceptedSections;
  const isAfterFeesPaid = isAfterOoDipAccepted(caseData?.status);
  const isPreDipStatus = !isPostDipPreFees && !isAfterFeesPaid;
  const showTermField = canAccessHppPages || (isPostDipPreFees && sidebarContext.canEditDipAcceptedSections);
  const isFormEditable = isOoFinancingFormEditable(
    caseData,
    canAccessHppPages,
    sidebarContext.canEditDipAcceptedSections,
  );
  const nextSection = getAdjacentOoCaseSidebarUrlSection(formSectionParam, sidebarContext, 'next') ?? '';

  if (isFormEditable && !formValidationContext) {
    throw new Error('OO financing form must be used within a FormValidationProvider when editable');
  }

  const setIsSubmitting = formValidationContext?.setIsSubmitting ?? (() => undefined);

  if (isCaseLoading || isFirmLoading) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    firm,
    isFormEditable,
    isDipAccepted: isDipAcceptedFullEdit,
    isDipAcceptedOrLater: isDipAcceptedFullEdit,
    intermediaryFees: caseData?.submissionRoute?.intermediaryFees ?? [],
    canAccessHppPages,
    showTermField,
    isIntermediaryFeesEditable: isDipAcceptedFullEdit,
  };

  const formSchema = buildOoFinancingFormColumns(ooFinancingFormSchema(schemaProps), {
    canEditAll: isDipAcceptedFullEdit || (Boolean(canAccessHppPages) && isPreDipStatus),
    canEditTermOnly: false,
    lockTermField: (canAccessHppPages || sidebarContext.canEditDipAcceptedSections) && isAfterFeesPaid,
  });

  return {
    isLoading: false as const,
    isFormEditable,
    footerProps: {
      previousSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'previous'),
      nextSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'next'),
      previousLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'previous'),
      nextLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'next'),
    },
    formProps: {
      columns: formSchema,
      isFormEditable,
      name: 'oo_financing',
      request: async () => {
        let brokerReferrerId: string | undefined;

        if (isSignedIn) {
          try {
            const user = await userApi.getUser();
            if (user?.id) {
              const brokerReferrer = await firmApi.getBrokerReferrerByUserId(user.id);
              brokerReferrerId = brokerReferrer.id;
            }
          } catch {
            brokerReferrerId = undefined;
          }
        }

        return hydrateOoFinancingFormValues(caseData, firm, brokerReferrerId);
      },
      onFinish: isFormEditable
        ? async (values: Record<string, unknown>) => {
            await submitFinancingAndPropertyForm({
              values,
              data: caseData,
              firm,
              id: caseId,
              propertyId,
              nextSection,
              navigate: () => undefined,
              onNavigateAfterSubmit: section => navigate({ search: `?section=${section}` }),
              queryClient,
              setIsSubmitting,
              message,
              showSuccessMessage: false,
            });
          }
        : undefined,
    },
  };
}
